import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronRight, Plus } from "lucide-react";

import styles from "./CompanyOrgStructure.module.css";
import { addRole, isRoleReferenced, removeRole, sortRolesForDisplay, updateRole } from "./orgStructureMutations";
import { MAX_ROLE_LABEL_LENGTH } from "./orgStructureSchema";
import { CompanyOrgStructureDocument } from "./orgStructureTypes";

interface OrgRoleManagerProps {
    document: CompanyOrgStructureDocument;
    onChange: (document: CompanyOrgStructureDocument) => void;
}

export function OrgRoleManager({ document, onChange }: OrgRoleManagerProps) {
    const { t, i18n } = useTranslation();
    const [newRoleLabel, setNewRoleLabel] = useState("");
    const [roleError, setRoleError] = useState<string | null>(null);

    const sortedRoles = useMemo(
        () => sortRolesForDisplay(document.roles, i18n.language),
        [document.roles, i18n.language]
    );

    const handleAddRole = () => {
        const label = newRoleLabel.trim();
        if (!label) {
            return;
        }
        try {
            const { document: next } = addRole(document, label);
            onChange(next);
            setNewRoleLabel("");
            setRoleError(null);
        } catch (err: unknown) {
            setRoleError(err instanceof Error ? err.message : t("settingsPage.errors.saveFailed"));
        }
    };

    const handleUpdateRole = (roleId: string, label: string) => {
        onChange(updateRole(document, roleId, label));
    };

    const handleRemoveRole = (roleId: string) => {
        if (isRoleReferenced(document, roleId)) {
            setRoleError(t("settingsPage.company.orgStructure.roles.inUse"));
            return;
        }
        try {
            onChange(removeRole(document, roleId));
            setRoleError(null);
        } catch (err: unknown) {
            setRoleError(err instanceof Error ? err.message : t("settingsPage.errors.saveFailed"));
        }
    };

    return (
        <div className={styles.rolesSection}>
            <details className={styles.rolesDisclosure}>
                <summary
                    className={styles.rolesSummary}
                    aria-label={t("settingsPage.company.orgStructure.roles.expandRoles")}
                >
                    <ChevronRight size={14} className={styles.rolesChevron} />
                    <span className={styles.rolesTitle}>{t("settingsPage.company.orgStructure.roles.title")}</span>
                    <span className={styles.rolesSummaryMeta}>
                        {t("settingsPage.company.orgStructure.roles.roleCount", {
                            count: document.roles.length
                        })}
                    </span>
                </summary>
                <div className={styles.rolesBody}>
                    {document.roles.length === 0 ? (
                        <p className={styles.rolesEmpty}>{t("settingsPage.company.orgStructure.roles.empty")}</p>
                    ) : (
                        <div className={styles.rolesList}>
                            {sortedRoles.map(role => (
                                <div key={role.id} className={styles.roleRow}>
                                    <input
                                        className={styles.roleInput}
                                        type="text"
                                        value={role.label}
                                        maxLength={MAX_ROLE_LABEL_LENGTH}
                                        onChange={event => handleUpdateRole(role.id, event.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className={styles.roleRemoveButton}
                                        disabled={isRoleReferenced(document, role.id)}
                                        onClick={() => handleRemoveRole(role.id)}
                                    >
                                        {t("settingsPage.company.orgStructure.roles.remove")}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </details>
            <div className={styles.roleAddRow}>
                <input
                    className={styles.roleInput}
                    type="text"
                    value={newRoleLabel}
                    maxLength={MAX_ROLE_LABEL_LENGTH}
                    placeholder={t("settingsPage.company.orgStructure.roles.placeholder")}
                    onChange={event => setNewRoleLabel(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            handleAddRole();
                        }
                    }}
                />
                <button type="button" className={styles.toolbarButton} onClick={handleAddRole}>
                    <Plus size={14} />
                    {t("settingsPage.company.orgStructure.roles.add")}
                </button>
            </div>
            {roleError ? <p className={styles.roleError}>{roleError}</p> : null}
        </div>
    );
}
